import React, { useMemo } from 'react';
import { RowSelectCheckbox } from '../components/bulk-action-bar';
import { isRowSelectable, OTHER_ORG_ROW_HINT } from '../utils/bulkSelection';
import { useActingAsEntity } from './useActingAsEntity';
import { useRowSelection } from './useRowSelection';

type UseBulkRowSelectionOptions<T extends { id: string }> = {
  rows: T[];
  /** Selection UI on/off (feature flag + permission). */
  enabled: boolean;
  /** List filter/search/sort signature; the selection clears when it changes. */
  resetKey: string;
  /** The row's owning organization, for the acting-as selectability rule. */
  getEntityId: (row: T) => string | null | undefined;
  /** Accessible name of a row's checkbox ("Select <label>"). */
  getLabel: (row: T) => string;
  /** Test ids: `<prefix>-select-<id>` per row, `<prefix>-select-all` in the header. */
  testIdPrefix: string;
};

/**
 * `useRowSelection` + the acting-as selectability rule + the checkbox controls a
 * list renders: `renderRowControl(row)` per row/card and `headerControl` for a
 * table's select-all (undefined when selection is off, so the list can drop the
 * checkbox column entirely).
 */
export function useBulkRowSelection<T extends { id: string }>({
  rows,
  enabled,
  resetKey,
  getEntityId,
  getLabel,
  testIdPrefix,
}: UseBulkRowSelectionOptions<T>) {
  const { actingAsEntity } = useActingAsEntity();
  const actingAsId = actingAsEntity?.id ?? null;
  const selection = useRowSelection(`${resetKey}|${actingAsId ?? ''}`);

  const selectableIds = useMemo(
    () => rows.filter((row) => isRowSelectable(getEntityId(row), actingAsId)).map((row) => row.id),
    [rows, getEntityId, actingAsId]
  );
  const allSelected = selection.areAllSelected(selectableIds);
  const someSelected = !allSelected && selectableIds.some((id) => selection.selected.has(id));

  const renderRowControl = (row: T): React.ReactNode => {
    if (!enabled) return null;
    const selectable = isRowSelectable(getEntityId(row), actingAsId);
    return (
      <RowSelectCheckbox
        checked={selection.selected.has(row.id)}
        onChange={() => selection.toggle(row.id)}
        disabled={!selectable}
        title={selectable ? undefined : OTHER_ORG_ROW_HINT}
        label={`Select ${getLabel(row)}`}
        data-testid={`${testIdPrefix}-select-${row.id}`}
      />
    );
  };

  let headerControl: React.ReactNode | undefined;
  if (enabled) {
    headerControl =
      selectableIds.length > 0 ? (
        <RowSelectCheckbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={() => selection.setMany(selectableIds, !allSelected)}
          label="Select all loaded rows"
          data-testid={`${testIdPrefix}-select-all`}
        />
      ) : null;
  }

  return { ...selection, actingAsEntity, selectableIds, renderRowControl, headerControl };
}
